import { useCallback, useState } from "react";

export interface RecentPage {
  path: string;
  label: string;
  section: string | null;
  visitedAt: number;
}

const STORAGE_KEY = "v-recent-pages";
const MAX_ENTRIES = 8;

function readStored(): RecentPage[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is RecentPage =>
        !!e &&
        typeof e.path === "string" &&
        typeof e.label === "string" &&
        typeof e.visitedAt === "number" &&
        (e.section === null || typeof e.section === "string")
    );
  } catch {
    return [];
  }
}

function writeStored(entries: RecentPage[]) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // localStorage unavailable (private mode / quota) — history just won't persist.
  }
}

/**
 * Tracks the last MAX_ENTRIES distinct pages visited, most-recent first.
 * Persisted to localStorage so it survives reloads/new tabs — unlike the
 * single-entry sessionStorage cross-section-back in useNavigationTrail,
 * which only remembers where the user came FROM, not a running history.
 */
export function useRecentPages() {
  const [entries, setEntries] = useState<RecentPage[]>(() => readStored());

  const record = useCallback(
    (path: string, label: string, section: string | null) => {
      setEntries(prev => {
        if (prev[0]?.path === path) return prev; // consecutive-duplicate guard
        const withoutDupes = prev.filter(e => e.path !== path);
        const next = [
          { path, label, section, visitedAt: Date.now() },
          ...withoutDupes,
        ].slice(0, MAX_ENTRIES);
        writeStored(next);
        return next;
      });
    },
    []
  );

  const clear = useCallback(() => {
    setEntries([]);
    writeStored([]);
  }, []);

  return { recentPages: entries, record, clear };
}
